/**
 * BroadcastsTab — Create and manage platform-wide announcements for tenants.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Megaphone, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { superAdminApi } from './superAdminApi';

const SEVERITY_COLORS = {
  info: 'bg-sky-500/20 text-sky-300 border-sky-400/30',
  success: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
  warning: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
  critical: 'bg-rose-500/20 text-rose-300 border-rose-400/30',
};

export const BroadcastsTab = () => {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: broadcasts = [], isLoading } = useQuery({
    queryKey: ['superadmin-broadcasts'],
    queryFn: superAdminApi.listBroadcasts,
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['superadmin-plans'],
    queryFn: () => superAdminApi.listPlans(false),
  });

  const delMut = useMutation({
    mutationFn: (id) => superAdminApi.deleteBroadcast(id),
    onSuccess: () => { toast.success('Broadcast deleted'); qc.invalidateQueries({ queryKey: ['superadmin-broadcasts'] }); },
  });

  return (
    <div className="space-y-4" data-testid="superadmin-broadcasts-tab">
      <div className="flex items-center">
        <div>
          <h3 className="text-lg font-semibold text-white">Platform Broadcasts</h3>
          <p className="text-xs text-slate-400">Announcements visible to all tenant admins on their dashboard</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} size="sm"
                className="ml-auto bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="create-broadcast-btn">
          <Plus className="h-4 w-4 mr-1" /> New Broadcast
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {isLoading && <p className="text-slate-500">Loading...</p>}
        {!isLoading && broadcasts.length === 0 && <p className="text-slate-500 text-sm col-span-2">No broadcasts yet.</p>}
        {broadcasts.map(b => (
          <Card key={b.id} className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-slate-400" />
                  <h4 className="font-bold">{b.title}</h4>
                </div>
                <Badge variant="outline" className={`capitalize ${SEVERITY_COLORS[b.severity] || ''}`}>
                  {b.severity}
                </Badge>
              </div>
              <p className="text-sm text-slate-300">{b.body}</p>
              <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                <span className="capitalize">Audience: {b.audience?.replace('_', ' ')}</span>
                {b.audience_filter?.length > 0 && <span>({b.audience_filter.join(', ')})</span>}
                <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {b.seen_count || 0} seen</span>
                {b.expires_at && <span>Expires {new Date(b.expires_at).toLocaleDateString()}</span>}
              </div>
              {b.cta_label && (
                <div className="pt-1">
                  <Badge variant="outline" className="bg-indigo-500/10 text-indigo-300 border-indigo-400/30">
                    CTA: {b.cta_label}{b.cta_url ? ` → ${b.cta_url}` : ''}
                  </Badge>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-white/5">
                <p className="text-xs text-slate-500">
                  By {b.created_by_email || 'unknown'} · {b.created_at ? new Date(b.created_at).toLocaleString() : ''}
                </p>
                <Button size="sm" variant="outline" onClick={() => {
                          if (confirm('Delete this broadcast?')) delMut.mutate(b.id);
                        }}
                        className="text-rose-300 border-rose-400/30 bg-rose-500/10 hover:bg-rose-500/20"
                        data-testid={`broadcast-delete-${b.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <CreateBroadcastDialog open={createOpen} onOpenChange={setCreateOpen} plans={plans}
                             onCreated={() => qc.invalidateQueries({ queryKey: ['superadmin-broadcasts'] })} />
    </div>
  );
};

const CreateBroadcastDialog = ({ open, onOpenChange, plans, onCreated }) => {
  const [form, setForm] = useState({
    title: '', body: '', severity: 'info', audience: 'all',
    audience_filter: [], cta_label: '', cta_url: '', expires_at: '',
  });
  const mut = useMutation({
    mutationFn: (data) => superAdminApi.createBroadcast(data),
    onSuccess: () => {
      toast.success('Broadcast published');
      onOpenChange(false);
      onCreated?.();
      setForm({ title: '', body: '', severity: 'info', audience: 'all', audience_filter: [], cta_label: '', cta_url: '', expires_at: '' });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  const toggleFilter = (v) => {
    setForm({ ...form, audience_filter: form.audience_filter.includes(v)
      ? form.audience_filter.filter(x => x !== v)
      : [...form.audience_filter, v] });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 text-white border-white/10 max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5" /> New Broadcast</DialogTitle>
          <DialogDescription className="text-slate-400">Visible to targeted tenant admins on their dashboards</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" data-testid="broadcast-title" />
          </div>
          <div><Label>Body *</Label>
            <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg p-2 text-sm text-white min-h-[90px]"
                      data-testid="broadcast-body" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Severity</Label>
              <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['info', 'success', 'warning', 'critical'].map(s =>
                    <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Audience</Label>
              <Select value={form.audience} onValueChange={v => setForm({ ...form, audience: v, audience_filter: [] })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="broadcast-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tenants</SelectItem>
                  <SelectItem value="by_plan">By plan</SelectItem>
                  <SelectItem value="by_status">By status</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.audience === 'by_plan' && (
            <div>
              <Label>Plans</Label>
              <div className="flex flex-wrap gap-1 pt-1">
                {plans.map(p => (
                  <Button key={p.code} type="button" size="sm"
                          variant={form.audience_filter.includes(p.code) ? 'default' : 'outline'}
                          onClick={() => toggleFilter(p.code)}
                          className={form.audience_filter.includes(p.code)
                            ? 'bg-indigo-500 text-white h-7'
                            : 'text-white border-white/20 bg-white/5 hover:bg-white/10 h-7'}>
                    {p.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {form.audience === 'by_status' && (
            <div>
              <Label>Statuses</Label>
              <div className="flex flex-wrap gap-1 pt-1">
                {['trial', 'active', 'suspended', 'expired'].map(s => (
                  <Button key={s} type="button" size="sm"
                          variant={form.audience_filter.includes(s) ? 'default' : 'outline'}
                          onClick={() => toggleFilter(s)}
                          className={form.audience_filter.includes(s) ? 'bg-indigo-500 text-white h-7 capitalize' : 'text-white border-white/20 bg-white/5 hover:bg-white/10 h-7 capitalize'}>
                    {s}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>CTA Label (optional)</Label>
              <Input value={form.cta_label} onChange={e => setForm({ ...form, cta_label: e.target.value })}
                     placeholder="e.g. Learn more" className="bg-white/5 border-white/10 text-white" />
            </div>
            <div><Label>CTA URL</Label>
              <Input value={form.cta_url} onChange={e => setForm({ ...form, cta_url: e.target.value })}
                     placeholder="/changelog" className="bg-white/5 border-white/10 text-white" />
            </div>
          </div>
          <div><Label>Expires At (optional)</Label>
            <Input type="datetime-local" value={form.expires_at}
                   onChange={e => setForm({ ...form, expires_at: e.target.value ? e.target.value + ':00Z' : '' })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="text-white border-white/20 bg-white/5">Cancel</Button>
          <Button onClick={() => mut.mutate(form)} disabled={mut.isPending || !form.title || !form.body}
                  className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="broadcast-submit-btn">
            {mut.isPending ? 'Publishing...' : 'Publish Broadcast'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
